/**
 * Closed-enum runtime guard helper.
 *
 * Sub-PR F R2 extracted the pattern from Sub-PR H's
 * `TraceTimeline::presentationFor` and Sub-PR F's
 * `ReviewQueuePage::badgeVariantFor`. Both files had identical
 * shape: a `Record<EnumValue, T>` table + a `KNOWN_VALUES: Set<string>`
 * + a `lookup(value: string) => T` helper that gated the cast on
 * Set membership and returned a fallback for unknown values.
 *
 * Why this is the right abstraction (rule of 3 — two concrete
 * uses + a clearly-defined contract):
 *
 *   COMPILE-TIME — `Record<EnumValue, T>` is total over the closed
 *     enum. Adding a value without a table entry fails the build
 *     at the consumer's table literal.
 *
 *   RUNTIME — `closedEnumLookup(map, fallback)` returns a function
 *     that admits unknown strings (backend ships a new variant
 *     before the frontend deploys) and routes them to `fallback`.
 *     The compile-time `Record` exhaustiveness claim stays honest
 *     because the runtime gate is the only path that produces a
 *     `T` for any given `string`.
 *
 * Usage:
 *
 *     const KIND_PRESENTATION: Record<MyKind, Cfg> = { ... };
 *     const UNKNOWN_CFG: Cfg = { ... };
 *     const presentationFor = closedEnumLookup(KIND_PRESENTATION, UNKNOWN_CFG);
 *     // …
 *     const cfg = presentationFor(event.kind);
 */
export function closedEnumLookup<K extends string, V>(
  map: Record<K, V>,
  fallback: V,
): (value: string) => V {
  // Keys of an `as const` literal-derived map are the enum's string
  // literals. `Object.keys` returns `string[]` per TS's index
  // signature; the Set's `string` element type keeps the runtime
  // gate independent of the compile-time `K` narrow.
  const known: ReadonlySet<string> = new Set<string>(Object.keys(map));
  return (value) => (known.has(value) ? map[value as K] : fallback);
}
